import { useState } from 'react';
import { toast } from 'react-toastify';
import { JobsiteService, JobsiteAddressUpdateDTO } from '../services/jobsite.service';
import googleMapsService from '../services/GoogleMaps.service';

/**
 * Shared hook for jobsite creation and editing
 * Handles geocoding and address save/update logic
 */
export const useJobsiteForm = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingText, setProcessingText] = useState('');

  /**
   * Geocode address and save for jobsite creation
   * Extracted from add-jobsite.tsx lines 293-324
   */
  const handleCreateJobsiteAddress = async (
    createdJobsite: any,
    addressDataDraft: any,
    formData: any,
    user: any
  ) => {
    try {
      if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        await JobsiteService.createJobsiteAddress({ ...addressDataDraft, job_id: createdJobsite.id, userId: user.id, coordinates: [] });
        toast.warning('Jobsite saved successfully, but geocoding failed. Address will not appear on the map.');
        return;
      }
      const google = await googleMapsService.loadMapsApi();
      const geocoder = new google.maps.Geocoder();
      const fullAddress = `${formData.address1}, ${formData.city}, ${formData.state}, ${formData.postalCode}, Canada`;
      setProcessingText('Geocoding address...');
      const coordinates = await new Promise<number[]>((resolve) => {
        geocoder.geocode({ address: fullAddress }, (results: any, status: any) => {
          const locationType = results?.[0]?.geometry?.location_type;
          const isAccurate = locationType === 'ROOFTOP' || locationType === 'RANGE_INTERPOLATED';
          console.log('[Geocode CREATE] status:', status, '| locationType:', locationType, '| isAccurate:', isAccurate, '| result:', results?.[0]);
          if (status === 'OK' && results[0] && isAccurate) {
            const location = results[0].geometry.location;
            resolve([location.lat(), location.lng()]);
          } else {
            toast.warning('Address could not be geocoded but is saved successfully in the application.');
            resolve([]);
          }
        });
      });
      // Create jobsite address
      // Prepare address data for saving
      const addressData = {
        ...addressDataDraft,
        job_id: createdJobsite.id,
        userId: user.id,
        coordinates: coordinates
      };
      // Failed Jobsite Address creation shouldn't prevent Jobsite Creation
      await JobsiteService.createJobsiteAddress(addressData);
      setProcessingText('Saving address...');
    } catch (addressError) {
      console.error('Error saving address:', addressError);
      toast.warning('Jobsite created, failed to create Jobsite Address. Please retry again later')
    }
  };

  /**
   * Geocode address and update for jobsite editing
   * Handles both creating new address (if missing) and updating existing address
   * Extracted from edit-jobsite.tsx lines 477-526
   */
  const handleUpdateJobsiteAddress = async (
    address: any,
    formData: any,
    user: any,
    jobId: string
  ) => {
    try {
      setProcessingText(address?.id ? 'Updating address...' : 'Creating address...');
      if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        const addressData = { address1: formData.address1, address2: formData.address2, city: formData.city, postalCode: formData.postalCode, province: formData.state, job_id: jobId, userId: user.id, coordinates: [] };
        address?.id ? await JobsiteService.updateJobsiteAddress(address.id, { ...addressData }) : await JobsiteService.createJobsiteAddress(addressData);
        toast.warning('Jobsite saved successfully, but geocoding failed. Address will not appear on the map.');
        return;
      }
      // Get coordinates for the address
      const google = await googleMapsService.loadMapsApi();
      const geocoder = new google.maps.Geocoder();
      const fullAddress = `${formData.address1}, ${formData.city}, ${formData.state}, ${formData.postalCode}, Canada`;

      const coordinates = await new Promise<number[]>((resolve) => {
        geocoder.geocode({ address: fullAddress }, (results: any, status: any) => {
          const locationType = results?.[0]?.geometry?.location_type;
          const isAccurate = locationType === 'ROOFTOP' || locationType === 'RANGE_INTERPOLATED';
          if (status === 'OK' && results[0] && isAccurate) {
            const location = results[0].geometry.location;
            resolve([location.lat(), location.lng()]);
          } else {
            toast.warning('Address could not be geocoded but is saved successfully in the application.');
            resolve([]);
          }
        });
      });

      // If address exists, update it. Otherwise, create it.
      if (address?.id) {
        // Update existing address
        const addressData: JobsiteAddressUpdateDTO = {
          address1: formData.address1,
          address2: formData.address2,
          city: formData.city,
          postalCode: formData.postalCode,
          province: formData.state,
          userId: user.id,
          job_id: jobId,
          coordinates: coordinates
        };
        await JobsiteService.updateJobsiteAddress(address.id, addressData);
      } else {
        // Create new address (handles case where address creation failed during jobsite creation)
        const addressData = {
          address1: formData.address1,
          address2: formData.address2,
          city: formData.city,
          postalCode: formData.postalCode,
          province: formData.state,
          job_id: jobId,
          userId: user.id,
          coordinates: coordinates
        };
        await JobsiteService.createJobsiteAddress(addressData);
      }
    } catch (addressError) {
      console.error('Address update failed:', addressError);
      toast.warning('Jobsite details updated, but address could not be saved.');
    }
  };

  return {
    isProcessing,
    setIsProcessing,
    processingText,
    setProcessingText,
    handleCreateJobsiteAddress,
    handleUpdateJobsiteAddress
  };
};
